/** The Integration tab (plan §1.1): how the booking's reservation system sees it —
 *  **What the feed sent** (the feed fingerprint the editor's restech warnings are built
 *  from, and the stored XML sliced to the booking), **Raw <restech> data** (a live read on
 *  demand beside the last webhook, with the difference — `console_bookings_restech_raw`,
 *  feed-managed bookings only), then the import runs that created and updated it and the
 *  webhook / API requests that touched it. Reimport from stored XML moved to the header's
 *  Actions menu (plan §3).
 *
 *  The Actions menu's "What the feed sent" / "View raw … data" and the time axis's import
 *  dots land here through `intent`: the section (or the run's row, tinted) scrolls into
 *  view and the section takes focus; "View raw" also asks for the live read when none is
 *  on screen. Each grid fetches when the tab opens. */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useBookingFeed } from '@/api/bookingActions';
import type { BookingRecordEditContext } from '@/api/bookingRecord';
import * as bookingsApi from '@/api/bookings';
import { SectionCard } from '@/components/ReadView';
import { EmbeddedApiRequests } from '@/features/api-requests/EmbeddedApiRequests';
import { EmbeddedImportRuns } from '@/features/import-runs/EmbeddedImportRuns';
import { usePermissions } from '@/hooks/usePermissions';
import { cond, importRunsHref } from '@/utils/gridLinks';

import { FEED_SECTION_ID, FeedSection } from './FeedSection';
import { RAW_SECTION_ID, RestechRawSection } from './RestechRawSection';
import { TabSection, useSectionIntent, type SectionIntent } from './TabSection';
import { TabSkeleton } from './TabSkeleton';

export const RESTECH_RAW_PERM = 'console_bookings_restech_raw';

export const IMPORTS_SECTION_ID = 'booking-integration-imports';

/** Where the Actions menu or the time axis sends the operator on this tab. */
export interface IntegrationIntent extends SectionIntent<'feed' | 'raw' | 'imports'> {
  /** Ask for the live raw read (when none is on screen). */
  fetch?: boolean;
}

const SECTION_IDS = { feed: FEED_SECTION_ID, raw: RAW_SECTION_ID, imports: IMPORTS_SECTION_ID };

const importRunsKey = (bookingId: number) => ['bookings', 'imports', bookingId] as const;

function ImportRuns({ bookingId, highlightId }: { bookingId: number; highlightId: string | null }) {
  const { data, isLoading, error } = useQuery({
    queryKey: importRunsKey(bookingId),
    queryFn: () => bookingsApi.listImportRuns(bookingId),
  });
  if (isLoading) return <TabSkeleton label="Loading import runs" />;
  if (error || !data) return <Alert severity="error">Couldn’t load import runs.</Alert>;
  return <EmbeddedImportRuns runs={data} highlightId={highlightId} />;
}

function ApiRequests({ bookingId }: { bookingId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bookings', 'api-requests', bookingId],
    queryFn: () => bookingsApi.listApiRequests(bookingId),
  });
  if (isLoading) return <TabSkeleton label="Loading API requests" />;
  if (error || !data) return <Alert severity="error">Couldn’t load API requests.</Alert>;
  return <EmbeddedApiRequests requests={data} />;
}

export function IntegrationTab({
  bookingId,
  editContext,
  intent = null,
  onIntentDone,
}: {
  bookingId: number;
  editContext: BookingRecordEditContext;
  intent?: IntegrationIntent | null;
  onIntentDone?: () => void;
}) {
  const { hasPerm } = usePermissions();
  const { restech } = editContext;
  const tz = editContext.schedule.timezone;
  const tenantId = editContext.tenant.id;
  const showRaw = Boolean(restech.managed_by) && hasPerm(RESTECH_RAW_PERM);

  // The feed section sits above the others: scroll once it has its content, or the
  // target moves down under the reader as the feed renders (and, for a run's row, once
  // the runs are there).
  const feedSettled = !useBookingFeed(bookingId).isLoading;
  const importsSettled = !useQuery({
    queryKey: importRunsKey(bookingId),
    queryFn: () => bookingsApi.listImportRuns(bookingId),
  }).isLoading;
  useSectionIntent(intent, SECTION_IDS, feedSettled && (intent?.section !== 'imports' || importsSettled), onIntentDone);
  // The row the time axis pointed at stays tinted after the scroll.
  const [highlight, setHighlight] = useState<string | null>(null);
  const pointed = intent?.section === 'imports' ? (intent.highlight ?? null) : undefined;
  useEffect(() => {
    if (pointed !== undefined) setHighlight(pointed);
  }, [pointed, intent?.seq]);

  return (
    <Box>
      <FeedSection bookingId={bookingId} timezone={tz} tenantId={tenantId} />
      {showRaw && (
        <RestechRawSection
          bookingId={bookingId}
          restech={restech}
          timezone={tz}
          fetchRequest={intent?.section === 'raw' && intent.fetch ? intent.seq : null}
        />
      )}
      <TabSection
        id={IMPORTS_SECTION_ID}
        title="Imports"
        action={
          hasPerm('console_import_runs_view') && (
            <Button
              size="small"
              component={RouterLink}
              to={importRunsHref([cond('participant', String(bookingId), 'exact')], tenantId)}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            >
              Open in Import runs
            </Button>
          )
        }
      >
        <ImportRuns bookingId={bookingId} highlightId={highlight} />
      </TabSection>
      <SectionCard title="API requests">
        <ApiRequests bookingId={bookingId} />
      </SectionCard>
    </Box>
  );
}
